/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import { PersonalisedSuggestionRequest, PlaintextApiResponse } from '@mta/vela-api-types'
import * as yup from 'yup'

import { useBaseForm } from '@/hooks/forms/useBaseForm'
import { submitPersonalisedSuggestionRequest } from '@/lib/api/contact/personalised-suggestion'

/** Yup validation schema used to validate the Personalised Suggestion form. */
const personalisedSuggestionFormSchema = yup.object().shape({
  userType: yup.string().required('Please select your user type.'),
  activityScope: yup.string().required('Please select an activity scope.'),
  learningObjective: yup.string().required('Please select a learning objective.'),
  deliveryMethod: yup.string().required('Please select a delivery method.'),
  duration: yup.string().required('Please select a duration.'),
  targetAudience: yup.string().required('Please select your target audience.'),
  additionalInfo: yup.string().max(8192, 'Additional information cannot exceed 8192 characters.'),
  name: yup.string().max(256, 'Name cannot exceed 256 characters.').required('Your name is required.'),
  email: yup
    .string()
    .email('Please enter a valid email address.')
    .max(128, 'Email cannot exceed 128 characters.')
    .required('Your work email is required.'),
})

/**
 * This hook is used to handle the Personalised Suggestion form.
 */
const usePersonalisedSuggestionForm = () => {
  const baseForm = useBaseForm<PersonalisedSuggestionRequest, PlaintextApiResponse>(
    personalisedSuggestionFormSchema,
    async (data) =>
      submitPersonalisedSuggestionRequest(
        data.userType,
        data.activityScope,
        data.learningObjective,
        data.deliveryMethod,
        data.duration,
        data.targetAudience,
        data.additionalInfo,
        data.name,
        data.email,
      ),
  )

  /**
   * Extends the base form onSubmit method to handle the request success state and reset the form after a successful
   * request.
   *
   * @param data - the data from the form
   */
  const extendedOnSubmit = async (data: PersonalisedSuggestionRequest) => {
    const result = await baseForm.onSubmit(data)

    if (result.success) {
      setTimeout(() => {
        // Clear the success state so the form can be filled in again
        baseForm.setRequestSuccess(false)
        baseForm.reset()
      }, 10000) // Reset after 10 seconds
    }
  }

  return {
    ...baseForm,
    handleSubmit: baseForm.handleSubmit(extendedOnSubmit),
  }
}

export { usePersonalisedSuggestionForm }
